import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import _flatten from 'lodash/flatten'
import _union from 'lodash/union'
import _uniqWith from 'lodash/uniqWith'
import _isEqual from 'lodash/isEqual'
import _isEmpty from 'lodash/isEmpty'

import { RootState } from 'app/store'
import { getDomainAuditLogs } from './api'
import { AUDIT_LOGS_REDUCER_NAME, LOGS_PER_PAGE } from './consts'
import type { DomainLogs } from 'features/audit/types'
import {
  getDomainsWhereUserIsAdminSelector,
  isRootAdminSelector,
} from 'features/auth/permissions/selectors'
import {
  checkUserIsAdmin,
  getUserDataFromDomain,
} from 'features/auth/permissions/utils'
import { currentRootDomainIdSelector } from 'features/rootDomains/selectors'

const initialState = {
  logs: [] as DomainLogs[],
  allLogsFetched: false,
}

const auditSlice = createSlice({
  name: AUDIT_LOGS_REDUCER_NAME,
  initialState,
  reducers: {
    clear: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(fetchAuditLogsThunk.fulfilled, (state, action) => {
      if (_isEmpty(action.payload)) {
        return {
          logs: state?.logs,
          allLogsFetched: true,
        }
      }
      const newLogs = _uniqWith(_union(state?.logs, action.payload), _isEqual)
      return {
        logs: newLogs,
        allLogsFetched: false,
      }
    })
  },
})

export const auditLogsReducer = auditSlice.reducer
export const auditLogsActions = auditSlice.actions

export const fetchAuditLogsThunk = createAsyncThunk<
  DomainLogs[],
  number,
  { state: RootState }
>('audit/fetchAuditLogs', async (page: number, { getState }) => {
  const state = getState()
  const domainsWhereUserIsAdmin = getDomainsWhereUserIsAdminSelector(state)
  const isRootAdmin = isRootAdminSelector(state)
  const currentRootDomainId = currentRootDomainIdSelector(state)
  try {
    if (isRootAdmin && currentRootDomainId) {
      const domainAuditLogsData = await getDomainAuditLogs(
        currentRootDomainId,
        {
          count: LOGS_PER_PAGE,
          page: page,
        }
      )

      return domainAuditLogsData?.data?.payload
    } else {
      // get logs from all subdomains where user is admin
      const domainsAuditLogs = await Promise.all(
        domainsWhereUserIsAdmin.map(async (domain) => {
          if (checkUserIsAdmin(getUserDataFromDomain(domain)) && domain?.id) {
            const domainAuditLogsData = await getDomainAuditLogs(domain?.id, {
              count: LOGS_PER_PAGE,
              page: page,
            })

            return domainAuditLogsData?.data?.payload
          }

          return null
        })
      )
      const aggregatedAuditLogs = _flatten(domainsAuditLogs).filter(
        (log) => log
      )

      return aggregatedAuditLogs
    }
  } catch (error) {
    console.log('Error fetching audit logs', error)
    throw error
  }
})
